/**
 * NgwEditorTheme — `IEditorTheme` 의 NgwEditor 본 구현.
 *
 * **0.4.17 네이티브 다크모드로 전환 (OMD-230).** 이전 레거시(TinyMCE 1:1 이식 — mount element 에
 * `ngw-dark` className 토글 + chroma luminance 색보정 `<style>` 주입)는 은퇴했다. 라이브러리가
 * 자체 CSS 변수(`[data-ngw-theme="dark"]` / `[data-ngw-content-theme="dark"]`)로 UI 크롬·본문을
 * 자족적으로 칠하므로, 어댑터는 boolean 만 넘기면 된다.
 *
 * 매핑:
 *   - `setDarkMode(isDark)`: 플래그 보존 + `lifecycle.setThemeMode(isDark)` 위임 → renderEditor 가
 *     `<Editor theme/contentTheme>` 로 reactive 반영(remount 없음). init 전 호출도 안전(lifecycle 이
 *     cachedCore 로 가드).
 *   - `setDarkBackColor` / `setDarkForeColor` / `removeDarkForeColor` / `getDark*Color`:
 *     **네이티브 theme 하에선 inert.** 라이브러리가 색을 관장하므로 이 값들은 소비되지 않는다.
 *     `IEditorTheme` 계약(다른 어댑터 호환)과 하위 호출부 보존을 위해 상태·luminance 저장만 유지한다.
 *     @deprecated 커스텀 다크 색이 필요해지면 라이브러리 theme 토큰 오버라이드로 대체한다.
 */

import chroma from 'chroma-js'
import type { IEditorTheme } from '../IEditorTheme'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

export class NgwEditorTheme implements IEditorTheme {
  private readonly adapter: NgwEditorAdapter
  private darkMode = false
  private backColor = ''
  private foreColor = ''
  /** 보존 — TinyMCE 의 darkModeInfo.backLum 와 동등. 네이티브 전환 후 inert (색 변환 호환성 보존용). */
  private backLum: number | null = null
  private foreLum: number | null = null

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  setDarkMode(isDark: boolean): void {
    if (this.darkMode === isDark) {
      return
    }
    this.darkMode = isDark
    // 네이티브 theme/contentTheme 으로 위임. lifecycle 이 init 전(cachedCore==null)엔 저장만,
    // 이후엔 같은 remountKey 재렌더로 reactive 반영한다(FOUC·remount 없음).
    this.adapter.lifecycle.setThemeMode(isDark)
  }

  isDarkMode(): boolean {
    return this.darkMode
  }

  getDarkBackColor(): string {
    return this.backColor
  }

  /** @deprecated 네이티브 theme 하에선 inert — 상태만 저장. */
  setDarkBackColor(color: string): void {
    this.backColor = color.toLowerCase()
    this.backLum = safeLuminance(this.backColor)
  }

  getDarkForeColor(): string {
    return this.foreColor
  }

  /** @deprecated 네이티브 theme 하에선 inert — 상태만 저장. */
  setDarkForeColor(color: string): void {
    this.foreColor = color.toLowerCase()
    this.foreLum = safeLuminance(this.foreColor)
  }

  /** @deprecated 네이티브 theme 하에선 inert — 상태만 초기화. */
  removeDarkForeColor(): void {
    this.foreColor = ''
    this.foreLum = null
  }

  /** Test 용 luminance 노출 — chroma 계산이 1:1 이전됐는지 검증. @internal */
  getBackLuminance(): number | null {
    return this.backLum
  }

  /** Test 용 luminance 노출. @internal */
  getForeLuminance(): number | null {
    return this.foreLum
  }
}

function safeLuminance(color: string): number | null {
  if (color.length === 0) {
    return null
  }
  try {
    return chroma(color).luminance()
  } catch {
    return null
  }
}
